// Layer 3 (Transformation): the route's status band and page chrome strings
// (locked comp `.impeccable/mocks/decision/planning.html`, variant A). Every
// figure is read from the existing Planning views; nothing is recomputed here.

import type { PlanningPeriod } from '@/versions/v1/schema/planning';
import type { SessionState } from '@/versions/v1/schema/scenario';
import { valueAt } from '@/versions/v1/shared/series.derive';
import { shortTonsToTonnes } from '@/versions/v1/shared/unit-conversion';
import { ANNUAL_STRETCH_MULTIPLIER, PLANNING_PERIODS } from '@/versions/v1/shared/bunker-hill-planning-seed';
import {
  formatPlanningMoney,
  formatSignedPercent,
  formatSignedPlanningMoney,
  type AnnualPlanView,
  type MonthlyPlanView,
  type PlanningTab,
  type RollingForecastView,
} from './Scene.transform';

/** A whole number of dry short tons, without the unit (the unit sits on the baseline). */
export function formatDst(value: number): string {
  return Math.round(value).toLocaleString('en-US');
}

export type PlanningBandCell = {
  id: 'to-date' | 'forecast' | 'nsr';
  name: string;
  figure: string;
  unit?: string;
  /** The comparison's lead term (600 ink-secondary) and the rest of it. */
  lead: string;
  leadTone: 'warn' | 'ink';
  rest: string;
  /** A second, quieter line: the window or the stretch reference. */
  note?: string;
  noteChip?: string;
  actor?: string;
  provenance: string;
};

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

function monthYear(dateIso: string): string {
  const [year, month] = dateIso.split('-').map(Number);
  return `${MONTH_ABBR[(month ?? 1) - 1] ?? ''} ${year}`;
}

/** The one annual window the header and the band read. */
function annualPeriod(): PlanningPeriod {
  const period = PLANNING_PERIODS.find((candidate) => candidate.type === 'annual');
  if (!period) throw new Error('Missing annual planning period');
  return period;
}

/** `Year 1 · Mar 2026 – Feb 2027`, from the annual planning window's own label
 *  and dates. */
export function planningPeriod(): { lead: string; range: string } {
  const period = annualPeriod();
  return {
    lead: period.label.split('—')[0]!.trim(),
    range: `${monthYear(period.startDate)} – ${monthYear(period.endDate)}`,
  };
}

/** The NSR settlement basis, kept off the glass in the NSR cell's provenance
 *  tooltip (it was the page subtitle's last clause). The feed is the measured
 *  reading at the session cursor, the same projection `/capacity` reads. */
function nsrBasisTooltip(state: SessionState): string {
  const feed = valueAt('rd-kpi-ore-td', state);
  if (feed === undefined) return 'NsrEngine · scenario throughput';
  const metric = shortTonsToTonnes(feed).toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `NsrEngine · scenario throughput · settles in metric tonnes (${feed.toLocaleString('en-US')} stpd = ${metric} metric t/d)`;
}

/** The months the year-to-date figure sums as measured actuals — `M1–M3` at
 *  the demo cursor, read off the same monthly view that names the M4 window. */
function measuredMonthRange(monthly: MonthlyPlanView): string {
  const measured = monthly.rows.filter((row) => row.actualSt !== undefined && row.actualWindowPlanSt === undefined);
  const first = measured[0];
  const last = measured[measured.length - 1];
  if (!first || !last) return '';
  const code = (id: string) => id.toUpperCase();
  return first === last ? code(first.id) : `${code(first.id)}–${code(last.id)}`;
}

export function planningBandCells(
  monthly: MonthlyPlanView,
  annual: AnnualPlanView,
  rolling: RollingForecastView,
  state: SessionState,
): PlanningBandCell[] {
  const central = rolling.scenarios.find((scenario) => scenario.name === 'Central')!;
  const measuredMonths = measuredMonthRange(monthly);
  return [
    {
      id: 'to-date',
      name: 'Year 1 to date',
      figure: formatDst(monthly.yearToDateActualSt),
      unit: 'dst',
      lead: formatSignedPercent(monthly.yearToDateVsBudgetPct),
      leadTone: monthly.yearToDateVsBudgetPct < 0 ? 'warn' : 'ink',
      rest: ` vs ${formatDst(monthly.yearToDateBudgetSt)} dst budget`,
      note: `${measuredMonths} actuals + ${monthly.mtdWindowLabel} measured feed`,
      provenance: `[derived] ${measuredMonths} actuals + ${monthly.mtdWindowLabel} daily series`,
    },
    {
      id: 'forecast',
      name: 'Year 1 forecast · central',
      figure: formatDst(central.fullYearTonnes),
      unit: 'dst',
      lead: `${monthly.forecastToBudgetPct.toFixed(1)}%`,
      leadTone: 'ink',
      rest: ` of ${formatDst(annual.annualBudgetSt)} dst budget`,
      note: `Stretch ${formatDst(annual.annualStretchSt)} dst · +${((ANNUAL_STRETCH_MULTIPLIER - 1) * 100).toFixed(0)}%`,
      noteChip: '[illustrative]',
      actor: `Engine · 7-day rate ${rolling.sevenDayStph.toFixed(1)} stph`,
      provenance: '[derived] actual-to-date + M4 balance + scheduled Year-1 hours',
    },
    {
      id: 'nsr',
      name: 'Year 1 NSR · central',
      figure: formatPlanningMoney(central.fullYearNsr),
      lead: formatSignedPlanningMoney(central.nsrVsBudget),
      leadTone: 'ink',
      rest: ` vs ${formatPlanningMoney(rolling.budgetNsr)} budget NSR`,
      actor: 'Engine · NSR',
      provenance: nsrBasisTooltip(state),
    },
  ];
}

/** The count beside each tab label (comp `.tn`): how many rows the tab draws. */
export function planningTabCounts(
  monthly: MonthlyPlanView,
  annual: AnnualPlanView,
  rolling: RollingForecastView,
): Record<PlanningTab, string> {
  return {
    monthly: `${monthly.rows.length} months`,
    annual: `${annual.rows.length} metrics`,
    forecast: `${rolling.scenarios.length} scenarios`,
  };
}
